import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ArrowLeft, X, Zap, Maximize2, Minimize2, RefreshCw, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';

/* ════════════════════════════════════════════════════════════════════════
   EDEN HQ — ufficio isometrico full-screen (stile Habbo).
   6 reparti reali, una scrivania per agente, lancio playbook via API.
   ════════════════════════════════════════════════════════════════════════ */

/* ── ISO MATH ──────────────────────────────────────────────────────────── */
const HW = 32, HH = 16;
const OX = 0,  OY = 210;
const WH = 210;            // wall height
const COLS = 30, ROWS = 22;

const iso      = (c, r) => ({ x: OX + (c - r) * HW, y: OY + (c + r) * HH });
const tilePts  = p => `${p.x},${p.y} ${p.x+HW},${p.y+HH} ${p.x},${p.y+2*HH} ${p.x-HW},${p.y+HH}`;
const tileFoot = (c, r) => { const p = iso(c, r); return { x: p.x, y: p.y + HH }; };
const zoneRect = (c1, r1, c2, r2) => {
  const a=iso(c1,r1), b=iso(c2+1,r1), p=iso(c2+1,r2+1), d=iso(c1,r2+1);
  return `${a.x},${a.y} ${b.x},${b.y} ${p.x},${p.y} ${d.x},${d.y}`;
};

const SORTED_TILES = [];
for (let d = 0; d <= (COLS-1)+(ROWS-1); d++)
  for (let c = 0; c < COLS; c++) { const r = d-c; if (r>=0 && r<ROWS) SORTED_TILES.push({c,r}); }

/* ── REPARTI (divisione ufficiale per codice agente) ───────────────────── */
const DEPTS = [
  { key:'comando',     emoji:'🧠', label:'COMANDO',            color:'#F59E0B',
    rect:{c1:12,r1:0,c2:17,r2:5},
    codes:['A-01','A-10','A-12'] },
  { key:'commerciale', emoji:'🎯', label:'COMMERCIALE & LEAD', color:'#10B981',
    rect:{c1:0,r1:0,c2:10,r2:6},
    codes:['A-02','A-03','A-04','A-05','A-06','A-09','A-18'] },
  { key:'tech',        emoji:'🛠️', label:'TECH & DATI',        color:'#3B82F6',
    rect:{c1:19,r1:0,c2:29,r2:6},
    codes:['A-11','A-13A','A-13B','A-20','A-21','A-29','A-30'] },
  { key:'direttori',   emoji:'🌍', label:'DIRETTORI B2B',      color:'#06B6D4',
    rect:{c1:0,r1:8,c2:10,r2:14},
    codes:['A-22','A-23','A-24','A-25','A-26','A-27'] },
  { key:'creative',    emoji:'🎨', label:'CONTENT & CREATIVE', color:'#EC4899',
    rect:{c1:12,r1:8,c2:22,r2:15},
    codes:['A-07','A-08','A-14','A-15','A-16','A-17','A-19','A-31','A-32','A-32B'] },
  { key:'specialisti', emoji:'💎', label:'SPECIALISTI',        color:'#A855F7',
    rect:{c1:24,r1:8,c2:29,r2:13},
    codes:['S-01','S-01B','S-01C','S-02'] },
];
const DEPT_BY_KEY  = Object.fromEntries(DEPTS.map(d=>[d.key,d]));
const CODE_TO_DEPT = {};
DEPTS.forEach(d => d.codes.forEach(c => { CODE_TO_DEPT[c] = d.key; }));

/* fallback per agenti senza codice mappato (es. Trinity, Neo) */
const deptOf = ag => {
  const code = String(ag.codice||'').trim().toUpperCase();
  if (CODE_TO_DEPT[code]) return CODE_TO_DEPT[code];
  const t = `${ag.nome||''} ${ag.ruolo||''}`.toLowerCase();
  if (/orchestr|coordinat|meta-agente|executive/.test(t)) return 'comando';
  if (/direttore commerciale/.test(t)) return 'direttori';
  if (/orolog|watch|reference/.test(t)) return 'specialisti';
  if (/architect|develop|dev|dati|kpi|rag|memoria|discord|notion|shopify|server|sistem|front-end|full stack/.test(t)) return 'tech';
  if (/sales|lead|vendita|closing|negozia|contratt|icebreak|interview|risk/.test(t)) return 'commerciale';
  return 'creative';
};

/* ── LAYOUT: scrivanie, postazioni, tile calpestabili per reparto ──────── */
const deskSlots = (rect, n) => {
  const out = [];
  for (let r = rect.r1+1; r <= rect.r2-1 && out.length < n; r += 2)
    for (let c = rect.c1+1; c <= rect.c2-1 && out.length < n; c += 2)
      out.push([c, r]);
  return out;
};

function buildLayout(agents) {
  const sorted = [...agents].sort((a,b) =>
    String(a.codice||'ZZ').localeCompare(String(b.codice||'ZZ')));
  const assign = {}, byDept = {};
  DEPTS.forEach(d => { byDept[d.key] = []; });
  sorted.forEach(ag => { const d = deptOf(ag); assign[ag.id] = d; byDept[d].push(ag); });

  const desks = {}, homes = {}, walkable = {};
  DEPTS.forEach(d => {
    const list = byDept[d.key];
    const ds   = deskSlots(d.rect, list.length);
    desks[d.key] = ds;
    const used = new Set(ds.map(s => s.join(',')));
    walkable[d.key] = [];
    for (let c = d.rect.c1; c <= d.rect.c2; c++)
      for (let r = d.rect.r1; r <= d.rect.r2; r++)
        if (!used.has(`${c},${r}`)) walkable[d.key].push([c, r]);
    list.forEach((ag, i) => {
      homes[ag.id] = i < ds.length
        ? [ds[i][0], ds[i][1] + 1]                       // davanti alla scrivania
        : walkable[d.key][(i*7) % walkable[d.key].length]; // overflow: in piedi
    });
  });
  return { assign, byDept, desks, homes, walkable };
}

/* ── PALETTES / AVATAR ─────────────────────────────────────────────────── */
const SHIRT_PAL  = ['#7C3AED','#0891B2','#059669','#D97706','#DC2626','#DB2777','#2563EB','#EA580C','#4F46E5','#65A30D','#9333EA','#0D9488','#1E40AF','#BE185D'];
const HAIR_PAL   = ['#18091E','#3B1C0C','#181818','#B36828','#5A3C0A','#C8A04A','#0A1E35','#A06020'];
const PANTS_PAL  = ['#252545','#1a1a30','#1E3A5F','#065F46','#7C2D12','#374151','#3D2B1F','#4A1942'];
const SHOES_PAL  = ['#131313','#5C3317','#D2691E','#EFEFEF','#8B0000','#2E4057','#1a2a1a','#704214'];
const HAIR_STYLES = ['buzz','short','medium','long','afro','bun','mohawk'];
const HAIR_LABELS = { buzz:'Rasato', short:'Corto', medium:'Medio', long:'Lungo', afro:'Afro', bun:'Chignon', mohawk:'Mohawk' };

const strHash = s => { let h=0; for(let i=0;i<(s||'').length;i++) h=(Math.imul(31,h)+s.charCodeAt(i))|0; return Math.abs(h); };

const defColors = uid => {
  const h = strHash(String(uid));
  return {
    shirt:     SHIRT_PAL[h % SHIRT_PAL.length],
    hair:      HAIR_PAL[(h>>3) % HAIR_PAL.length],
    hairStyle: HAIR_STYLES[(h>>5) % HAIR_STYLES.length],
    pants:     PANTS_PAL[(h>>7) % PANTS_PAL.length],
    shoes:     SHOES_PAL[(h>>9) % SHOES_PAL.length],
    gender:    (h % 3 === 0) ? 'f' : 'm',
  };
};

const loadChar = uid => {
  try {
    const s = JSON.parse(localStorage.getItem(`eden-char-${uid}`));
    return s ? { ...defColors(uid), ...s } : null;
  } catch { return null; }
};
const saveChar = (uid, d) => localStorage.setItem(`eden-char-${uid}`, JSON.stringify(d));

/* playbook ↔ agente: match per nome o codice nel campo pb.agenti */
const pbMatchesAgent = (pb, ag) => {
  const t = (pb.agenti || '').toLowerCase();
  if (!t) return false;
  const nome = (ag.nome || '').toLowerCase();
  if (nome && t.includes(nome)) return true;
  if (ag.codice && t.includes(String(ag.codice).toLowerCase())) return true;
  const first = nome.split(/[\s_]+/)[0];
  return first && first.length > 3 && t.includes(first);
};

/* ── ISO BOX ───────────────────────────────────────────────────────────── */
function IsoBox({ c, r, elev, top, right, left }) {
  const p = iso(c, r);
  const T=[p.x,p.y], R=[p.x+HW,p.y+HH], B=[p.x,p.y+2*HH], L=[p.x-HW,p.y+HH];
  const j = (...vv) => vv.map(v => v.join(',')).join(' ');
  return (
    <g>
      <polygon points={j([T[0],T[1]-elev],[R[0],R[1]-elev],[B[0],B[1]-elev],[L[0],L[1]-elev])} fill={top} stroke="rgba(0,0,0,0.12)" strokeWidth={0.5}/>
      <polygon points={j([R[0],R[1]-elev],[B[0],B[1]-elev],B,R)} fill={right} stroke="rgba(0,0,0,0.12)" strokeWidth={0.5}/>
      <polygon points={j([L[0],L[1]-elev],[B[0],B[1]-elev],B,L)} fill={left} stroke="rgba(0,0,0,0.12)" strokeWidth={0.5}/>
    </g>
  );
}

/* ── DESK (scrivania + monitor acceso colore reparto) ──────────────────── */
function Desk({ c, r, screen }) {
  const p = iso(c, r);
  return (
    <g>
      <IsoBox c={c} r={r} elev={18} top="#9B6E42" right="#6A4020" left="#7E5230"/>
      <IsoBox c={c} r={r} elev={33} top="#1A2A3E" right="#0F1A28" left="#152232"/>
      <rect x={p.x-6} y={p.y-33} width={12} height={9}
        fill={screen} opacity={0.35} stroke={screen} strokeWidth={0.5}/>
    </g>
  );
}

/* ── CHARACTER SVG (piedi in (0,0)) ────────────────────────────────────── */
function CharSVG({ shirt='#7C3AED', hair='#181818', hairStyle='short', pants='#252545', shoes='#131313', gender='m', isMe=false }) {
  const S=1.32, X0=-11*S, Y0=-38*S;
  const x=px=>X0+px*S, y=py=>Y0+py*S, w=n=>n*S;
  const skin='#FDBCB4', oc='#1a0808', sw=1.2, isF=gender==='f';

  const renderHair = () => {
    if (hairStyle==='buzz') return (
      <rect x={x(3)} y={y(1.5)} width={w(16)} height={w(2.5)} rx={S*.5} fill={hair} stroke={oc} strokeWidth={sw*.8}/>
    );
    if (hairStyle==='medium') return (<>
      <rect x={x(3)}  y={y(0)} width={w(16)} height={w(5)}  rx={S} fill={hair} stroke={oc} strokeWidth={sw}/>
      <rect x={x(1)}  y={y(1)} width={w(4)}  height={w(10)} rx={S} fill={hair} stroke={oc} strokeWidth={sw}/>
      <rect x={x(17)} y={y(1)} width={w(4)}  height={w(10)} rx={S} fill={hair} stroke={oc} strokeWidth={sw}/>
    </>);
    if (hairStyle==='long') return (<>
      <rect x={x(3)}  y={y(0)} width={w(16)} height={w(5)}  rx={S} fill={hair} stroke={oc} strokeWidth={sw}/>
      <rect x={x(1)}  y={y(1)} width={w(4)}  height={w(21)} rx={S} fill={hair} stroke={oc} strokeWidth={sw}/>
      <rect x={x(17)} y={y(1)} width={w(4)}  height={w(21)} rx={S} fill={hair} stroke={oc} strokeWidth={sw}/>
    </>);
    if (hairStyle==='afro') return (
      <ellipse cx={x(11)} cy={y(0)} rx={w(12)} ry={w(9)} fill={hair} stroke={oc} strokeWidth={sw}/>
    );
    if (hairStyle==='bun') return (<>
      <rect x={x(3)} y={y(2)} width={w(16)} height={w(2.5)} rx={S*.5} fill={hair} stroke={oc} strokeWidth={sw*.8}/>
      <ellipse cx={x(11)} cy={y(-2.5)} rx={w(4.5)} ry={w(4)} fill={hair} stroke={oc} strokeWidth={sw}/>
    </>);
    if (hairStyle==='mohawk') return (<>
      <rect x={x(3)} y={y(2.5)} width={w(16)} height={w(2)}  rx={S*.4} fill={hair} stroke={oc} strokeWidth={sw*.7}/>
      <rect x={x(9)} y={y(-5)}  width={w(4)}  height={w(9)}  rx={S*.8} fill={hair} stroke={oc} strokeWidth={sw}/>
    </>);
    return (<>
      <rect x={x(3)} y={y(0)} width={w(16)} height={w(5)} rx={S} fill={hair} stroke={oc} strokeWidth={sw}/>
      <rect x={x(2)} y={y(1)} width={w(3)}  height={w(7)} rx={S} fill={hair} stroke={oc} strokeWidth={sw}/>
    </>);
  };

  return (
    <g style={{ filter: isMe ? `drop-shadow(0 0 7px ${shirt})` : undefined }}>
      <ellipse cx={0} cy={2} rx={w(9)} ry={w(2.4)} fill="rgba(0,0,0,0.22)"/>
      {renderHair()}
      <rect x={x(3)} y={y(2)} width={w(16)} height={w(13)} rx={w(2)} fill={skin} stroke={oc} strokeWidth={sw+.1}/>
      <rect x={x(6)}    y={y(7)} width={w(3)} height={w(3)} fill={oc}/>
      <rect x={x(13)}   y={y(7)} width={w(3)} height={w(3)} fill={oc}/>
      <rect x={x(7.5)}  y={y(7)} width={w(1)} height={w(1)} fill="white"/>
      <rect x={x(14.5)} y={y(7)} width={w(1)} height={w(1)} fill="white"/>
      {isF && (<>
        <line x1={x(5)}    y1={y(6)} x2={x(7)}    y2={y(7)} stroke={oc} strokeWidth={.9}/>
        <line x1={x(9.5)}  y1={y(6)} x2={x(8.5)}  y2={y(7)} stroke={oc} strokeWidth={.9}/>
        <line x1={x(12)}   y1={y(6)} x2={x(13)}   y2={y(7)} stroke={oc} strokeWidth={.9}/>
        <line x1={x(16.5)} y1={y(6)} x2={x(15.5)} y2={y(7)} stroke={oc} strokeWidth={.9}/>
      </>)}
      <rect x={x(8)} y={y(12)} width={w(6)} height={w(2)} rx={w(.8)} fill={isF?'#E87090':'#C47060'}/>
      <rect x={x(4)}  y={y(16)} width={w(14)} height={w(11)} rx={w(1.5)} fill={shirt} stroke={oc} strokeWidth={sw+.1}/>
      <path d={`M${x(7)},${y(16)} L${x(11)},${y(20)} L${x(15)},${y(16)}`} fill="none" stroke="rgba(255,255,255,.28)" strokeWidth={sw*.7}/>
      <rect x={x(0)}  y={y(16)} width={w(5)}  height={w(9)} rx={w(1.5)} fill={shirt} stroke={oc} strokeWidth={sw}/>
      <rect x={x(17)} y={y(16)} width={w(5)}  height={w(9)} rx={w(1.5)} fill={shirt} stroke={oc} strokeWidth={sw}/>
      <rect x={x(0)}  y={y(24)} width={w(5)}  height={w(3.5)} rx={w(1)} fill={skin} stroke={oc} strokeWidth={sw*.8}/>
      <rect x={x(17)} y={y(24)} width={w(5)}  height={w(3.5)} rx={w(1)} fill={skin} stroke={oc} strokeWidth={sw*.8}/>
      {isF ? (<>
        <path d={`M${x(3)},${y(27)} L${x(19)},${y(27)} L${x(21)},${y(34)} L${x(1)},${y(34)}`} fill={pants} stroke={oc} strokeWidth={sw+.1}/>
        <rect x={x(4.5)}  y={y(34)} width={w(5)} height={w(4)} fill={skin} stroke={oc} strokeWidth={sw*.7}/>
        <rect x={x(12.5)} y={y(34)} width={w(5)} height={w(4)} fill={skin} stroke={oc} strokeWidth={sw*.7}/>
      </>) : (<>
        <rect x={x(4)} y={y(27)} width={w(14)} height={w(7)} rx={w(1)} fill={pants} stroke={oc} strokeWidth={sw+.1}/>
        <line x1={x(11)} y1={y(27)} x2={x(11)} y2={y(34)} stroke={oc} strokeWidth={sw*.8}/>
      </>)}
      <rect x={x(3)}  y={y(34)} width={w(7)} height={w(4)} rx={w(1.5)} fill={shoes}/>
      <rect x={x(12)} y={y(34)} width={w(7)} height={w(4)} rx={w(1.5)} fill={shoes}/>
    </g>
  );
}

/* ── SPEECH BUBBLE (lavoro in corso) ───────────────────────────────────── */
function WorkBubble({ code }) {
  const txt = `PB ${code}`;
  const w = Math.max(40, txt.length * 6.5 + 16);
  return (
    <g className="wbPing">
      <rect x={-w/2} y={-78} width={w} height={17} rx={8.5}
        fill="rgba(7,7,26,0.92)" stroke="#00D4FF" strokeWidth={1}/>
      <polygon points="-4,-61 4,-61 0,-55" fill="rgba(7,7,26,0.92)" stroke="#00D4FF" strokeWidth={1}/>
      <circle cx={-w/2+10} cy={-69.5} r={2.6} fill="#00D4FF" className="wbDot"/>
      <text x={4} y={-66} textAnchor="middle" fill="#00D4FF" fontSize={8.5} fontWeight={700} fontFamily="monospace">{txt}</text>
    </g>
  );
}

/* ── ROOM SVG ──────────────────────────────────────────────────────────── */
const WALL_ANG = Math.atan2(HH, HW) * 180 / Math.PI;   // 26.57°

function RoomSVG({ chars, layout, busyMap, highlight, onCharClick, onWbClick, pbCount, pbBusy }) {
  const [wbHov, setWbHov] = useState(false);
  const svgRef = useRef(null);
  const [vb, setVb] = useState({ x:-790, y:-60, w:1830, h:1170 });
  const drag = useRef(null);

  const clientToSvg = useCallback((cx, cy) => {
    const svg = svgRef.current;
    if (!svg) return { x:0, y:0 };
    const pt = svg.createSVGPoint();
    pt.x = cx; pt.y = cy;
    const m = svg.getScreenCTM();
    if (!m) return { x:0, y:0 };
    const p = pt.matrixTransform(m.inverse());
    return { x:p.x, y:p.y };
  }, []);

  const onWheel = useCallback(e => {
    e.preventDefault();
    const f = e.deltaY > 0 ? 1.12 : 1/1.12;
    setVb(prev => {
      const nw = Math.min(2600, Math.max(380, prev.w * f));
      const k  = nw / prev.w;
      const p  = clientToSvg(e.clientX, e.clientY);
      return { x: p.x - (p.x - prev.x) * k, y: p.y - (p.y - prev.y) * k, w: nw, h: prev.h * k };
    });
  }, [clientToSvg]);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    el.addEventListener('wheel', onWheel, { passive:false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  /* drag-pan SENZA pointer capture (la capture rubava i click a personaggi/lavagna) */
  const onPointerDown = e => {
    if (e.button !== undefined && e.button !== 0) return;
    drag.current = { sx:e.clientX, sy:e.clientY, vb:{...vb}, moved:false };
  };
  const onPointerMove = e => {
    if (!drag.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const dx = (e.clientX - drag.current.sx) * (vb.w / rect.width);
    const dy = (e.clientY - drag.current.sy) * (vb.h / rect.height);
    if (Math.abs(e.clientX-drag.current.sx) + Math.abs(e.clientY-drag.current.sy) > 5) drag.current.moved = true;
    setVb({ ...drag.current.vb, x: drag.current.vb.x - dx, y: drag.current.vb.y - dy });
  };
  const endDrag = () => {
    const moved = drag.current?.moved;
    drag.current = null;
    return moved;
  };
  const movedRef = useRef(false);
  const onPointerUp = () => { movedRef.current = endDrag(); };
  const safeClick = fn => { if (!movedRef.current) fn(); };

  /* pareti: back wall lungo r=0, left wall lungo c=0 */
  const backTL = { x: iso(0,0).x,    y: iso(0,0).y - WH };
  const backTR = { x: iso(COLS,0).x, y: iso(COLS,0).y - WH };
  const backBR = iso(COLS,0), backBL = iso(0,0);
  const leftTL = { x: iso(0,ROWS).x, y: iso(0,ROWS).y - WH };
  const leftBL = iso(0,ROWS), leftBR = iso(0,0);

  /* parametriche pareti: u lungo la parete, v dall'alto (0) al basso (1) */
  const bwP = (u, v) => ({ x: backTL.x + u*(backTR.x-backTL.x), y: backTL.y + u*(backTR.y-backTL.y) + v*WH });
  const lwP = (u, v) => ({ x: leftBR.x + u*(leftTL.x-leftBR.x+0), y: (leftBR.y - WH) + u*(leftTL.y-(leftBR.y-WH)) + v*WH });

  /* insegna EDEN HQ — centro back wall */
  const s1 = bwP(.40,.04), s2 = bwP(.60,.04), s3 = bwP(.60,.40), s4 = bwP(.40,.40);
  const signPts = `${s1.x},${s1.y} ${s2.x},${s2.y} ${s3.x},${s3.y} ${s4.x},${s4.y}`;
  const sCX = (s1.x+s2.x+s3.x+s4.x)/4, sCY = (s1.y+s2.y+s3.y+s4.y)/4;

  /* lavagna playbook — left wall */
  const wb = { tl:lwP(.06,.12), tr:lwP(.30,.12), br:lwP(.30,.66), bl:lwP(.06,.66) };
  const wbPts = `${wb.tl.x},${wb.tl.y} ${wb.tr.x},${wb.tr.y} ${wb.br.x},${wb.br.y} ${wb.bl.x},${wb.bl.y}`;
  const wbCX = (wb.tl.x+wb.tr.x+wb.br.x+wb.bl.x)/4;
  const wbCY = (wb.tl.y+wb.tr.y+wb.br.y+wb.bl.y)/4;

  /* finestre back wall */
  const winPts = (u1, u2) => {
    const a=bwP(u1,.10), b=bwP(u2,.10), c=bwP(u2,.52), d=bwP(u1,.52);
    return `${a.x},${a.y} ${b.x},${b.y} ${c.x},${c.y} ${d.x},${d.y}`;
  };
  const WINDOWS = [[.045,.135],[.18,.27],[.66,.75],[.80,.89]];

  return (
    <svg ref={svgRef} viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`} width="100%" height="100%"
      preserveAspectRatio="xMidYMid meet"
      style={{ display:'block', touchAction:'none', cursor:'grab' }}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove}
      onPointerUp={onPointerUp} onPointerLeave={onPointerUp}>
      <defs>
        <style>{`
          .cBob{animation:cBob 2.6s ease-in-out infinite}
          @keyframes cBob{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}
          .ehqPulse{animation:ehqPulse 3.2s ease-in-out infinite}
          @keyframes ehqPulse{0%,100%{opacity:.82}50%{opacity:1}}
          .wbPing{animation:wbPingA 2s ease-in-out infinite}
          @keyframes wbPingA{0%,100%{opacity:.85}50%{opacity:1}}
          .wbDot{animation:wbDotA 1.1s ease-in-out infinite}
          @keyframes wbDotA{0%,100%{opacity:1}50%{opacity:.25}}
          .charWalk{transition:transform 1.9s cubic-bezier(.45,.05,.55,.95)}
          .starTw{animation:starTw 3.4s ease-in-out infinite}
          @keyframes starTw{0%,100%{opacity:.9}50%{opacity:.25}}
        `}</style>
        <filter id="neonBlur" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="5"/>
        </filter>
        <filter id="softBlur" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.5"/>
        </filter>
        <radialGradient id="led1" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.9"/><stop offset="100%" stopColor="#3B82F6" stopOpacity="0"/>
        </radialGradient>
        <radialGradient id="led2" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#10B981" stopOpacity="0.9"/><stop offset="100%" stopColor="#10B981" stopOpacity="0"/>
        </radialGradient>
        <linearGradient id="winSky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0B1B3A"/><stop offset="100%" stopColor="#1A0F3A"/>
        </linearGradient>
      </defs>

      {/* ── BG ── */}
      <rect x={-2200} y={-1200} width={6000} height={3600} fill="#0D0D1F"/>

      {/* ── LEFT WALL ── */}
      <polygon points={`${leftBR.x},${leftBR.y-WH} ${leftTL.x},${leftTL.y} ${leftBL.x},${leftBL.y} ${leftBR.x},${leftBR.y}`} fill="#B85510"/>
      <polygon points={`${lwP(0,0).x},${lwP(0,0).y} ${lwP(1,0).x},${lwP(1,0).y} ${lwP(1,.04).x},${lwP(1,.04).y} ${lwP(0,.04).x},${lwP(0,.04).y}`} fill="#E8882A" opacity={0.5}/>
      <polygon points={`${lwP(0,.96).x},${lwP(0,.96).y} ${lwP(1,.96).x},${lwP(1,.96).y} ${lwP(1,1).x},${lwP(1,1).y} ${lwP(0,1).x},${lwP(0,1).y}`} fill="#7A3808" opacity={0.5}/>

      {/* ── BACK WALL ── */}
      <polygon points={`${backTL.x},${backTL.y} ${backTR.x},${backTR.y} ${backBR.x},${backBR.y} ${backBL.x},${backBL.y}`} fill="#A04A0A"/>
      <polygon points={`${bwP(0,0).x},${bwP(0,0).y} ${bwP(1,0).x},${bwP(1,0).y} ${bwP(1,.04).x},${bwP(1,.04).y} ${bwP(0,.04).x},${bwP(0,.04).y}`} fill="#D4721C" opacity={0.5}/>
      <polygon points={`${bwP(0,.96).x},${bwP(0,.96).y} ${bwP(1,.96).x},${bwP(1,.96).y} ${bwP(1,1).x},${bwP(1,1).y} ${bwP(0,1).x},${bwP(0,1).y}`} fill="#6A2E08" opacity={0.5}/>
      <line x1={backBL.x} y1={backBL.y-WH} x2={backBL.x} y2={backBL.y} stroke="#5C2408" strokeWidth={2}/>

      {/* ── FINESTRE (notte, stelle) ── */}
      {WINDOWS.map(([u1,u2],wi)=>(
        <g key={wi}>
          <polygon points={winPts(u1-0.008,u2+0.008)} fill="#3A1A05"/>
          <polygon points={winPts(u1,u2)} fill="url(#winSky)" stroke="rgba(0,212,255,0.18)" strokeWidth={0.8}/>
          {[0,1,2,3,4].map(si=>{
            const su = u1 + ((si*0.19 + wi*0.07) % 1) * (u2-u1);
            const sv = .15 + ((si*0.13 + wi*0.11) % 1) * .3;
            const p = bwP(su, sv);
            return <circle key={si} cx={p.x} cy={p.y} r={1.3} fill="#E8F4FF" className="starTw"
              style={{ animationDelay:`${(si+wi)*0.55}s` }}/>;
          })}
        </g>
      ))}

      {/* ── EDEN HQ NEON SIGN ── */}
      <polygon points={signPts} fill="#07071A" stroke="rgba(0,212,255,0.35)" strokeWidth={1.5}/>
      <g className="ehqPulse">
        <text x={sCX} y={sCY} textAnchor="middle" dominantBaseline="middle"
          fill="rgba(0,212,255,0.55)" stroke="rgba(0,212,255,0.55)" strokeWidth={18}
          fontSize={42} fontWeight={900} fontFamily="'Arial Black','Impact',sans-serif"
          filter="url(#neonBlur)" transform={`rotate(${WALL_ANG},${sCX},${sCY})`}>EDEN HQ</text>
        <text x={sCX} y={sCY} textAnchor="middle" dominantBaseline="middle"
          fill="none" stroke="rgba(0,212,255,0.7)" strokeWidth={5}
          fontSize={42} fontWeight={900} fontFamily="'Arial Black','Impact',sans-serif"
          filter="url(#softBlur)" transform={`rotate(${WALL_ANG},${sCX},${sCY})`}>EDEN HQ</text>
        <text x={sCX} y={sCY} textAnchor="middle" dominantBaseline="middle"
          fill="rgba(200,245,255,0.95)" stroke="#00D4FF" strokeWidth={1.5}
          fontSize={42} fontWeight={900} fontFamily="'Arial Black','Impact',sans-serif"
          transform={`rotate(${WALL_ANG},${sCX},${sCY})`}>EDEN HQ</text>
      </g>

      {/* ── LAVAGNA PLAYBOOK (left wall, cliccabile) ── */}
      <g onClick={()=>safeClick(onWbClick)}
        onMouseEnter={()=>setWbHov(true)} onMouseLeave={()=>setWbHov(false)}
        style={{ cursor:'pointer' }}>
        <polygon points={wbPts} fill="#0A1520"
          stroke={wbHov?'#00D4FF':'rgba(0,212,255,0.5)'} strokeWidth={wbHov?2.5:1.4}/>
        {[.28,.44,.60,.76].map((v,i)=>(
          <line key={i} x1={lwP(.09,v).x} y1={lwP(.09,v).y} x2={lwP(.27,v).x} y2={lwP(.27,v).y}
            stroke="rgba(0,212,255,0.18)" strokeWidth={0.8} strokeDasharray="3 3"/>
        ))}
        <text x={wbCX} y={wbCY-22} textAnchor="middle" dominantBaseline="middle"
          fill={wbHov?'#00D4FF':'rgba(0,212,255,0.75)'} fontSize={13} fontWeight={800} fontFamily="monospace" letterSpacing={2}
          transform={`rotate(${-WALL_ANG},${wbCX},${wbCY-22})`}>📋 PLAYBOOK</text>
        {pbCount > 0 && (<>
          <circle cx={wb.tr.x+14} cy={wb.tr.y+10} r={11}
            fill={pbBusy>0?'rgba(245,158,11,0.18)':'rgba(0,212,255,0.15)'}
            stroke={pbBusy>0?'rgba(245,158,11,0.6)':'rgba(0,212,255,0.5)'} strokeWidth={1}/>
          <text x={wb.tr.x+14} y={wb.tr.y+14} textAnchor="middle"
            fill={pbBusy>0?'#F59E0B':'#00D4FF'} fontSize={10} fontWeight={700}>{pbBusy>0?pbBusy:pbCount}</text>
        </>)}
        <text x={wbCX} y={wbCY+34} textAnchor="middle" fill="rgba(0,212,255,0.65)"
          fontSize={8} fontFamily="monospace" transform={`rotate(${-WALL_ANG},${wbCX},${wbCY+34})`}>
          {wbHov ? '▶ clicca per aprire' : `${pbCount} disponibili`}
        </text>
      </g>

      {/* ── PAVIMENTO ── */}
      {SORTED_TILES.map(({c,r}) => (
        <polygon key={`t${c}-${r}`} points={tilePts(iso(c,r))}
          fill={(c+r)%2===0?'#EEE8D8':'#E0D8C4'} stroke="rgba(0,0,0,0.05)" strokeWidth={0.5}/>
      ))}

      {/* ── TAPPETI REPARTO + ETICHETTE ── */}
      {DEPTS.map(d=>(
        <polygon key={d.key} points={zoneRect(d.rect.c1,d.rect.r1,d.rect.c2,d.rect.r2)}
          fill={`${d.color}1E`} stroke={`${d.color}60`} strokeWidth={1.2} strokeDasharray="6 4"/>
      ))}
      {DEPTS.map(d=>{
        const mc=(d.rect.c1+d.rect.c2+1)/2, mr=d.rect.r2+0.6;
        const cx=OX+(mc-mr)*HW, cy=OY+(mc+mr)*HH;
        const n = (layout.byDept[d.key]||[]).length;
        return (
          <g key={`lb${d.key}`}>
            <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle"
              fill={d.color} fontSize={12} fontWeight={800} letterSpacing={2} fontFamily="system-ui,sans-serif"
              paintOrder="stroke" stroke="rgba(0,0,0,0.55)" strokeWidth={3}>
              {d.emoji} {d.label} · {n}
            </text>
          </g>
        );
      })}

      {/* ── LOUNGE (zona relax in basso) ── */}
      <polygon points={zoneRect(8,17,21,21)} fill="rgba(255,255,255,0.045)"
        stroke="rgba(255,255,255,0.14)" strokeWidth={1} strokeDasharray="6 4"/>
      {(()=>{ const mc=15, mr=22.4; const cx=OX+(mc-mr)*HW, cy=OY+(mc+mr)*HH;
        return <text x={cx} y={cy} textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize={11}
          fontWeight={700} letterSpacing={3} fontFamily="system-ui,sans-serif">☕ LOUNGE</text>; })()}
      {/* divano */}
      {[[11,19],[12,19]].map(([c,r])=>(
        <IsoBox key={`sf${c}`} c={c} r={r} elev={12} top="#7C3AED" right="#4C1D95" left="#5B21B6"/>
      ))}
      <IsoBox c={13} r={19} elev={24} top="#6D28D9" right="#4C1D95" left="#5B21B6"/>
      {/* tavolino ping-pong */}
      {[[16,19],[17,19]].map(([c,r])=>(
        <IsoBox key={`pp${c}`} c={c} r={r} elev={15} top="#0E7490" right="#155E75" left="#0C4A6E"/>
      ))}
      {(()=>{ const a=iso(17,19); return <line x1={a.x-HW} y1={a.y+HH-15} x2={a.x+HW} y2={a.y+HH-15} stroke="white" strokeWidth={1.2} opacity={0.7}/>; })()}
      {/* macchina caffè */}
      <IsoBox c={9} r={18} elev={30} top="#2A2A3E" right="#16161F" left="#202030"/>
      {(()=>{ const p=iso(9,18); return (<>
        <rect x={p.x-5} y={p.y-26} width={10} height={7} rx={1} fill="rgba(245,158,11,0.45)"/>
        <circle cx={p.x+8} cy={p.y-22} r={1.8} fill="#10B981" className="wbDot"/>
      </>); })()}

      {/* ── SCRIVANIE PER REPARTO (una per agente) ── */}
      {DEPTS.map(d=>(
        <g key={`dk${d.key}`}>
          {(layout.desks[d.key]||[]).map(([c,r],i)=>(
            <Desk key={i} c={c} r={r} screen={d.color}/>
          ))}
        </g>
      ))}

      {/* ── DECOR REPARTI ── */}
      {/* Tech: server rack in fondo */}
      <IsoBox c={28} r={6} elev={55} top="#121830" right="#08101E" left="#0E1428"/>
      {[0,1,2,3,4].map(i=>{
        const p=iso(28,6), lx=p.x+HW-4, ly=p.y+HH-(55-14)-i*9;
        return (<g key={i}>
          <circle cx={lx}   cy={ly} r={2.2} fill="#3B82F6" opacity={0.9}/>
          <circle cx={lx}   cy={ly} r={5}   fill="url(#led1)"/>
          <circle cx={lx+5} cy={ly} r={2.2} fill="#10B981" opacity={0.9}/>
          <circle cx={lx+5} cy={ly} r={5}   fill="url(#led2)"/>
        </g>);
      })}
      {/* Specialisti: vetrina orologi */}
      <IsoBox c={29} r={13} elev={40} top="#1E1B3A" right="#12102A" left="#181534"/>
      {(()=>{ const p=iso(29,13); return (<>
        <rect x={p.x-7} y={p.y-36} width={14} height={20} rx={2}
          fill="rgba(168,85,247,0.12)" stroke="rgba(168,85,247,0.5)" strokeWidth={0.8}/>
        <circle cx={p.x} cy={p.y-26} r={4} fill="none" stroke="#E2C044" strokeWidth={1.3}/>
        <line x1={p.x} y1={p.y-26} x2={p.x} y2={p.y-29} stroke="#E2C044" strokeWidth={1}/>
        <line x1={p.x} y1={p.y-26} x2={p.x+2.4} y2={p.y-26} stroke="#E2C044" strokeWidth={1}/>
      </>); })()}
      {/* piante agli angoli */}
      {[[11,3,'#059669'],[18,3,'#10B981'],[11,12,'#0D9488'],[23,12,'#059669'],[5,16,'#10B981'],[24,16,'#0D9488'],[2,20,'#059669'],[27,20,'#10B981']].map(([c,r,col])=>{
        const f=tileFoot(c,r);
        return (
          <g key={`pl${c}-${r}`}>
            <polygon points={`${f.x-5},${f.y-10} ${f.x+5},${f.y-10} ${f.x+4},${f.y} ${f.x-4},${f.y}`}
              fill="#8B4513" stroke="#5C3317" strokeWidth={0.5}/>
            <ellipse cx={f.x}   cy={f.y-21} rx={9} ry={8} fill={col} opacity={0.9}/>
            <ellipse cx={f.x-7} cy={f.y-16} rx={5} ry={4} fill={col} opacity={0.7} transform={`rotate(-30,${f.x-7},${f.y-16})`}/>
            <ellipse cx={f.x+7} cy={f.y-16} rx={5} ry={4} fill={col} opacity={0.7} transform={`rotate(30,${f.x+7},${f.y-16})`}/>
          </g>
        );
      })}

      {/* ── PERSONAGGI ── */}
      {chars.map(({u, foot, col}) => {
        const delay  = (strHash(String(u.id)) % 30) / 10;
        const active = u.stato === 'attivo';
        const busy   = busyMap[u.id];
        const dim    = highlight && !highlight.has(u.id);
        const raw    = (u.nome||'?').replace(/^Eden\s+/i,'').replace(/_/g,' ');
        const label  = raw.length > 14 ? raw.slice(0,13)+'…' : raw;
        return (
          <g key={u.id} className="charWalk"
            style={{ transform:`translate(${foot.x}px, ${foot.y}px)`,
              cursor:'pointer', opacity: dim ? 0.18 : (active ? 1 : 0.55) }}
            onClick={()=>safeClick(()=>onCharClick(u))}>
            <g className="cBob" style={{ animationDelay:`${delay}s` }}>
              <CharSVG shirt={col.shirt} hair={col.hair} hairStyle={col.hairStyle}
                pants={col.pants} shoes={col.shoes} gender={col.gender}
                isMe={!!busy}/>
              {busy && <WorkBubble code={busy}/>}
              <circle cx={12} cy={-38*1.32+4} r={3.5}
                fill={busy?'#F59E0B':active?'#10B981':u.stato==='in_pausa'?'#F59E0B':'#6B7280'}
                stroke="rgba(0,0,0,0.4)" strokeWidth={0.8}/>
            </g>
            <text x={0} y={11} textAnchor="middle"
              fontSize={8} fontWeight={700} fontFamily="system-ui,sans-serif"
              fill="white" paintOrder="stroke" stroke="rgba(0,0,0,0.75)" strokeWidth={3}>
              {label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* ── HUD CHIP ──────────────────────────────────────────────────────────── */
function Chip({ label, value, color='#00D4FF' }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:7, background:'rgba(7,7,26,0.78)',
      border:'1px solid rgba(255,255,255,0.09)', borderRadius:9, padding:'5px 12px', backdropFilter:'blur(6px)' }}>
      <span style={{ fontSize:10, color:'rgba(255,255,255,0.45)', textTransform:'uppercase', letterSpacing:'.06em', fontWeight:600 }}>{label}</span>
      <span style={{ fontSize:14, fontWeight:800, color }}>{value}</span>
    </div>
  );
}

/* ── AGENT SIDE PANEL ──────────────────────────────────────────────────── */
function AgentPanel({ ag, col, dept, playbooks, busyMap, lanciando, onLancia, onCustomize, onClose, isAdmin }) {
  const myPbs = playbooks.filter(pb => pbMatchesAgent(pb, ag));
  const d = DEPT_BY_KEY[dept];
  return (
    <div style={{ position:'absolute', top:64, right:12, bottom:12, width:'min(360px, calc(100vw - 24px))',
      background:'rgba(13,13,31,0.94)', border:'1px solid rgba(0,212,255,0.18)', borderRadius:14,
      backdropFilter:'blur(10px)', boxShadow:'0 14px 48px rgba(0,0,0,0.6)', zIndex:30,
      display:'flex', flexDirection:'column', overflow:'hidden', animation:'panelIn .2s ease-out' }}>
      <style>{`@keyframes panelIn{from{transform:translateX(30px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>
      <div style={{ display:'flex', alignItems:'center', gap:13, padding:'16px 18px 12px', borderBottom:'1px solid rgba(255,255,255,0.07)', flexShrink:0 }}>
        <div style={{ background:'rgba(255,255,255,0.04)', borderRadius:10, padding:'6px 2px', flexShrink:0 }}>
          <svg viewBox="-25 -86 50 92" width={46} height={84}>
            <CharSVG shirt={col.shirt} hair={col.hair} hairStyle={col.hairStyle}
              pants={col.pants} shoes={col.shoes} gender={col.gender}/>
          </svg>
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:15.5, fontWeight:800, color:'#fff' }}>{(ag.nome||'').replace(/_/g,' ')}</div>
          {ag.ruolo && <div style={{ fontSize:11.5, color:'rgba(255,255,255,0.55)', marginTop:2, lineHeight:1.4 }}>{ag.ruolo}</div>}
          <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, flexWrap:'wrap' }}>
            {ag.codice && <span style={{ fontFamily:'monospace', fontSize:10.5, color:'#00D4FF', background:'rgba(0,212,255,0.1)', padding:'1px 7px', borderRadius:5 }}>{ag.codice}</span>}
            {d && <span style={{ fontSize:10.5, color:d.color, background:`${d.color}1A`, padding:'1px 7px', borderRadius:5, fontWeight:700 }}>{d.emoji} {d.label}</span>}
            <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:10.5, color:'rgba(255,255,255,0.55)' }}>
              <span style={{ width:7, height:7, borderRadius:'50%', display:'inline-block',
                background: busyMap[ag.id]?'#F59E0B':ag.stato==='attivo'?'#10B981':ag.stato==='in_pausa'?'#F59E0B':'#6B7280' }}/>
              {busyMap[ag.id] ? `al lavoro su PB ${busyMap[ag.id]}` : (ag.stato||'inattivo')}
            </span>
          </div>
        </div>
        <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(255,255,255,0.45)', display:'flex', padding:4, alignSelf:'flex-start' }}><X size={16}/></button>
      </div>

      <div style={{ flex:1, overflowY:'auto', padding:'14px 18px' }}>
        {[{lb:'Personalità',v:ag.personalita},{lb:'Superpower',v:ag.superpower},{lb:'Descrizione',v:ag.descrizione}].map(({lb,v})=>v&&(
          <div key={lb} style={{ marginBottom:13 }}>
            <div style={{ fontSize:9.5, fontWeight:700, color:'rgba(255,255,255,0.35)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:3 }}>{lb}</div>
            <div style={{ fontSize:12.5, color:'rgba(255,255,255,0.72)', lineHeight:1.6 }}>{v}</div>
          </div>
        ))}

        <div style={{ fontSize:9.5, fontWeight:700, color:'rgba(255,255,255,0.35)', textTransform:'uppercase', letterSpacing:'.06em', margin:'16px 0 8px' }}>
          Playbook ({myPbs.length})
        </div>
        {myPbs.length === 0 && (
          <div style={{ fontSize:12, color:'rgba(255,255,255,0.35)', fontStyle:'italic' }}>Nessun playbook assegnato · attivazione on-demand</div>
        )}
        {myPbs.map(pb=>(
          <div key={pb.id} style={{ background:'rgba(255,255,255,0.035)', border:'1px solid rgba(255,255,255,0.07)',
            borderRadius:9, padding:'9px 12px', marginBottom:7, display:'flex', alignItems:'center', gap:9 }}>
            <span style={{ fontFamily:'monospace', fontSize:10.5, color:'#00D4FF', background:'rgba(0,212,255,0.1)', padding:'1px 7px', borderRadius:5, flexShrink:0 }}>{pb.codice}</span>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:12, fontWeight:600, color:'#fff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{pb.nome}</div>
              {pb.trigger_attivo && <div style={{ fontSize:9.5, color:'#F59E0B', marginTop:1 }}>⚙ in corso</div>}
            </div>
            {isAdmin && (
            <button onClick={()=>onLancia(pb)} disabled={!!lanciando || pb.trigger_attivo}
              style={{ fontSize:10.5, fontWeight:700, color: pb.trigger_attivo?'rgba(255,255,255,0.3)':'#00D4FF',
                background: pb.trigger_attivo?'rgba(255,255,255,0.04)':'rgba(0,212,255,0.1)',
                border:'1px solid rgba(0,212,255,0.2)', borderRadius:6, padding:'4px 9px',
                cursor: (lanciando||pb.trigger_attivo)?'not-allowed':'pointer', display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
              <Zap size={10}/>{lanciando===pb.id?'…':pb.trigger_attivo?'attivo':'Lancia'}
            </button>
            )}
          </div>
        ))}
      </div>

      <div style={{ padding:'12px 18px', borderTop:'1px solid rgba(255,255,255,0.07)', flexShrink:0 }}>
        <button onClick={onCustomize} style={{
          width:'100%', padding:'9px', borderRadius:8,
          border:'1px solid rgba(0,212,255,0.4)', cursor:'pointer',
          background:'rgba(0,212,255,0.07)', color:'#00D4FF', fontWeight:600, fontSize:12.5,
        }}>🎨 Personalizza aspetto</button>
      </div>
    </div>
  );
}

/* ── PLAYBOOK DRAWER ───────────────────────────────────────────────────── */
function PlaybookDrawer({ playbooks, lanciando, onLancia, onClose, isAdmin }) {
  const [open, setOpen] = useState(null);
  const busy = playbooks.filter(p=>p.trigger_attivo).length;
  return (
    <>
      <div onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.45)', zIndex:40 }}/>
      <div style={{ position:'absolute', top:0, right:0, bottom:0, width:'min(480px,100vw)',
        background:'rgba(13,13,31,0.97)', borderLeft:'1px solid rgba(0,212,255,0.15)',
        display:'flex', flexDirection:'column', boxShadow:'-10px 0 48px rgba(0,0,0,0.6)', zIndex:41,
        backdropFilter:'blur(10px)', animation:'slideIn .22s ease-out' }}>
        <style>{`@keyframes slideIn{from{transform:translateX(100%)}to{transform:translateX(0)}}`}</style>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'18px 20px', borderBottom:'1px solid rgba(255,255,255,0.07)', flexShrink:0 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:'#fff' }}>📋 Playbook</div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.45)', marginTop:2 }}>
              {playbooks.length} disponibili{busy>0 && <span style={{ color:'#F59E0B' }}> · {busy} in corso</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(255,255,255,0.45)', display:'flex', padding:4 }}><X size={17}/></button>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'12px 16px', display:'flex', flexDirection:'column', gap:7 }}>
          {playbooks.length===0 && <div style={{ padding:40, textAlign:'center', color:'rgba(255,255,255,0.35)', fontSize:13 }}>Nessun playbook</div>}
          {playbooks.map((pb,i)=>(
            <div key={pb.id||i} onClick={()=>setOpen(open===i?null:i)}
              style={{ background:'rgba(255,255,255,0.035)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10,
                padding:'11px 14px', cursor:'pointer',
                borderLeft: pb.trigger_attivo ? '3px solid #F59E0B' : open===i?'3px solid #00D4FF':'3px solid transparent',
                transition:'border-color .15s' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontFamily:'monospace', fontSize:11, color:'#00D4FF', background:'rgba(0,212,255,0.1)',
                  padding:'2px 7px', borderRadius:4, flexShrink:0, border:'1px solid rgba(0,212,255,0.2)' }}>
                  {pb.codice||`PB-${i+1}`}
                </span>
                <span style={{ fontSize:13, fontWeight:600, color:'#fff', flex:1, minWidth:0,
                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {pb.nome||'Playbook'}
                </span>
                {pb.trigger_attivo && <span style={{ fontSize:9.5, color:'#F59E0B', flexShrink:0 }}>⚙ in corso</span>}
                <span style={{ fontSize:10, color:'rgba(255,255,255,0.3)', flexShrink:0 }}>{open===i?'▲':'▼'}</span>
              </div>
              {open===i && (
                <div style={{ marginTop:10, paddingTop:10, borderTop:'1px solid rgba(255,255,255,0.07)', display:'flex', flexDirection:'column', gap:7 }}>
                  {pb.descrizione && <div style={{ fontSize:12.5, color:'rgba(255,255,255,0.6)', lineHeight:1.65 }}>{pb.descrizione}</div>}
                  {pb.funzione && <div><div style={{ fontSize:9.5, fontWeight:700, color:'rgba(255,255,255,0.35)', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:3 }}>Funzione</div><div style={{ fontSize:12, color:'rgba(255,255,255,0.6)', lineHeight:1.5 }}>{pb.funzione}</div></div>}
                  {pb.agenti && <div style={{ fontSize:11, color:'#c084fc' }}>{pb.agenti}</div>}
                  {pb.comando && <div style={{ fontFamily:'monospace', fontSize:11.5, color:'#00D4FF', background:'rgba(0,0,0,0.35)', padding:'5px 10px', borderRadius:6 }}>⌨ {pb.comando}</div>}
                  {isAdmin && (
                  <button onClick={e=>{ e.stopPropagation(); onLancia(pb); }} disabled={!!lanciando || pb.trigger_attivo}
                    style={{ alignSelf:'flex-start', fontSize:11.5, fontWeight:700,
                      color: pb.trigger_attivo?'rgba(255,255,255,0.3)':'#00D4FF',
                      background: pb.trigger_attivo?'rgba(255,255,255,0.04)':'rgba(0,212,255,0.1)',
                      border:'1px solid rgba(0,212,255,0.25)', borderRadius:7, padding:'6px 14px',
                      cursor:(lanciando||pb.trigger_attivo)?'not-allowed':'pointer', display:'flex', alignItems:'center', gap:5, marginTop:2 }}>
                    <Zap size={11}/>{lanciando===pb.id?'Lancio…':pb.trigger_attivo?'Già in corso':'Lancia →'}
                  </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/* ── CHARACTER CUSTOMIZER ──────────────────────────────────────────────── */
function CharCustomizer({ uid, agName, initial, onSave, onClose }) {
  const [cfg, setCfg] = useState(initial);
  const set = (k, v) => setCfg(prev => ({...prev, [k]: v}));

  const PalRow = ({ label, pal, fk }) => (
    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:10, fontWeight:700, color:'rgba(255,255,255,0.4)', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:7 }}>{label}</div>
      <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
        {pal.map(c=>(
          <button key={c} onClick={()=>set(fk,c)} style={{
            width:24, height:24, borderRadius:'50%', background:c, cursor:'pointer', flexShrink:0,
            border: cfg[fk]===c ? '2.5px solid white' : '2px solid transparent',
            outline: cfg[fk]===c ? `2px solid ${c}` : 'none', outlineOffset:2, padding:0,
          }}/>
        ))}
      </div>
    </div>
  );

  return (
    <div onClick={e=>e.target===e.currentTarget&&onClose()}
      style={{ position:'absolute', inset:0, background:'rgba(10,10,18,0.72)', zIndex:60,
        display:'flex', alignItems:'center', justifyContent:'center', padding:16, overflowY:'auto' }}>
      <div style={{ background:'rgba(13,13,31,0.98)', border:'1px solid rgba(0,212,255,0.18)', borderRadius:16,
        padding:'22px 24px', width:'min(420px,100%)', maxHeight:'92%', overflowY:'auto',
        boxShadow:'0 14px 48px rgba(0,0,0,0.65)', position:'relative' }}>
        <button onClick={onClose} style={{ position:'absolute', top:14, right:16, background:'none', border:'none', cursor:'pointer', color:'rgba(255,255,255,0.45)', display:'flex', padding:4 }}>
          <X size={16}/>
        </button>
        <div style={{ fontSize:15, fontWeight:700, color:'#fff', marginBottom:2 }}>Personalizza aspetto</div>
        <div style={{ fontSize:12, color:'rgba(255,255,255,0.5)', marginBottom:16 }}>{agName}</div>

        <div style={{ display:'flex', justifyContent:'center', marginBottom:18, background:'rgba(0,0,0,0.3)', borderRadius:10, padding:'12px 0' }}>
          <svg viewBox="-25 -86 50 92" width={62} height={110}>
            <CharSVG shirt={cfg.shirt} hair={cfg.hair} hairStyle={cfg.hairStyle}
              pants={cfg.pants} shoes={cfg.shoes} gender={cfg.gender}/>
          </svg>
        </div>

        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:10, fontWeight:700, color:'rgba(255,255,255,0.4)', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:7 }}>Sesso</div>
          <div style={{ display:'flex', gap:8 }}>
            {[['m','Uomo'],['f','Donna']].map(([g,lbl])=>(
              <button key={g} onClick={()=>set('gender',g)} style={{
                flex:1, padding:'7px 0', borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:12.5,
                border: cfg.gender===g ? '2px solid #00D4FF' : '1px solid rgba(255,255,255,0.12)',
                background: cfg.gender===g ? 'rgba(0,212,255,0.1)' : 'rgba(255,255,255,0.04)',
                color: cfg.gender===g ? '#00D4FF' : 'rgba(255,255,255,0.55)',
              }}>{lbl}</button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:10, fontWeight:700, color:'rgba(255,255,255,0.4)', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:7 }}>Taglio di capelli</div>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            {HAIR_STYLES.map(hs=>(
              <button key={hs} onClick={()=>set('hairStyle',hs)} style={{
                padding:'5px 10px', borderRadius:7, cursor:'pointer', fontSize:11.5, fontWeight:600,
                border: cfg.hairStyle===hs ? '2px solid #00D4FF' : '1px solid rgba(255,255,255,0.12)',
                background: cfg.hairStyle===hs ? 'rgba(0,212,255,0.1)' : 'rgba(255,255,255,0.04)',
                color: cfg.hairStyle===hs ? '#00D4FF' : 'rgba(255,255,255,0.55)',
              }}>{HAIR_LABELS[hs]}</button>
            ))}
          </div>
        </div>

        <PalRow label="Colore capelli"    pal={HAIR_PAL}  fk="hair"/>
        <PalRow label="Maglietta"         pal={SHIRT_PAL} fk="shirt"/>
        <PalRow label="Pantaloni / Gonna" pal={PANTS_PAL} fk="pants"/>
        <PalRow label="Scarpe"            pal={SHOES_PAL} fk="shoes"/>

        <button onClick={()=>{ saveChar(uid,cfg); onSave(uid,cfg); onClose(); }} style={{
          width:'100%', padding:'11px', borderRadius:8, border:'none', cursor:'pointer',
          background:'#00D4FF', color:'#000', fontWeight:700, fontSize:13, marginTop:4,
        }}>Salva aspetto</button>
      </div>
    </div>
  );
}

/* ── PAGE ──────────────────────────────────────────────────────────────── */
export default function Gamification() {
  const navigate = useNavigate();
  const rootRef  = useRef(null);
  const { user } = useAuth();
  // Solo admin/boss possono lanciare playbook; i non-admin vedono l'ufficio in sola lettura.
  const isAdmin  = user?.ruolo === 'admin' || user?.ruolo === 'boss';

  const [agents,     setAgents]     = useState([]);
  const [playbooks,  setPlaybooks]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showPb,     setShowPb]     = useState(false);
  const [selectedAg, setSelectedAg] = useState(null);
  const [custUid,    setCustUid]    = useState(null);
  const [charColors, setCharColors] = useState({});
  const [positions,  setPositions]  = useState({});   // agentId -> [c,r]
  const [lanciando,  setLanciando]  = useState(null);
  const [toast,      setToast]      = useState(null);
  const [query,      setQuery]      = useState('');
  const [feed,       setFeed]       = useState([]);
  const [isFs,       setIsFs]       = useState(false);
  const [clock,      setClock]      = useState(new Date());

  const layout = useMemo(() => buildLayout(agents), [agents]);

  /* ── data load + polling ── */
  const loadData = useCallback((first=false) => {
    Promise.all([
      api.get('/eden_hq/agenti').catch(()=>({data:[]})),
      api.get('/eden_hq/playbook').catch(()=>({data:[]})),
      api.get('/logs', { params:{ azione:'LANCIA_PLAYBOOK', limit:8 }, silent:true }).catch(()=>({data:{logs:[]}})),
    ]).then(([a, p, l]) => {
      const al = a.data || [];
      setAgents(al);
      setPlaybooks(p.data || []);
      setFeed(l.data?.logs || []);
      if (first) {
        const cols = {};
        al.forEach(ag => { cols[ag.id] = loadChar(ag.id) || defColors(ag.id); });
        setCharColors(cols);
        const lay = buildLayout(al);
        const pos = {};
        al.forEach(ag => { pos[ag.id] = lay.homes[ag.id]; });
        setPositions(pos);
        setLoading(false);
      }
    });
  }, []);

  useEffect(() => { loadData(true); }, [loadData]);
  useEffect(() => {
    const t = setInterval(() => loadData(false), 20000);
    return () => clearInterval(t);
  }, [loadData]);

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 15000);
    return () => clearInterval(t);
  }, []);

  /* ── passeggiata: chi è alla scrivania ogni tanto fa due passi e torna ── */
  useEffect(() => {
    if (loading || !Object.keys(layout.homes).length) return;
    const t = setInterval(() => {
      setPositions(prev => {
        const ids = Object.keys(prev);
        if (!ids.length) return prev;
        const next = { ...prev };
        const occupied = new Set(ids.map(id => next[id]?.join(',')));
        const movers = 2 + Math.floor(Math.random()*3);
        for (let m = 0; m < movers; m++) {
          const id = ids[Math.floor(Math.random()*ids.length)];
          const cur  = next[id], home = layout.homes[id];
          const dept = layout.assign[id];
          if (!cur || !home || !dept) continue;
          const atHome = cur[0]===home[0] && cur[1]===home[1];
          let target = null;
          if (!atHome) {
            /* torna alla scrivania se libera */
            if (!occupied.has(home.join(','))) target = home;
          } else if (Math.random() < 0.55) {
            /* due passi nel reparto */
            const cands = layout.walkable[dept].filter(s => {
              const k = s.join(',');
              if (occupied.has(k)) return false;
              const dist = Math.abs(s[0]-cur[0]) + Math.abs(s[1]-cur[1]);
              return dist > 0 && dist <= 4;
            });
            if (cands.length) target = cands[Math.floor(Math.random()*cands.length)];
          }
          if (!target) continue;
          occupied.delete(cur.join(','));
          occupied.add(target.join(','));
          next[id] = target;
        }
        return next;
      });
    }, 4200);
    return () => clearInterval(t);
  }, [loading, layout]);

  /* ── ESC chiude pannelli ── */
  useEffect(() => {
    const h = e => {
      if (e.key !== 'Escape') return;
      if (custUid) setCustUid(null);
      else if (showPb) setShowPb(false);
      else if (selectedAg) setSelectedAg(null);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [custUid, showPb, selectedAg]);

  /* ── fullscreen browser ── */
  useEffect(() => {
    const h = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);
  const toggleFs = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else rootRef.current?.requestFullscreen?.();
  };

  /* ── lancio playbook ── */
  const lancia = async (pb) => {
    if (lanciando) return;
    setLanciando(pb.id);
    try {
      await api.post('/eden_hq/lancia', { playbook_id: pb.id });
      setToast({ ok:true, msg:`Playbook ${pb.codice} · ${pb.nome} lanciato — Claude lo raccoglie al prossimo polling` });
      loadData(false);
    } catch(e) {
      setToast({ ok:false, msg: e.response?.data?.detail || 'Errore nel lancio del playbook' });
    }
    setLanciando(null);
    setTimeout(() => setToast(null), 5000);
  };

  /* ── derivati ── */
  const busyMap = useMemo(() => {
    const m = {};
    playbooks.filter(p=>p.trigger_attivo).forEach(pb => {
      agents.forEach(ag => { if (!m[ag.id] && pbMatchesAgent(pb, ag)) m[ag.id] = pb.codice; });
    });
    return m;
  }, [playbooks, agents]);

  const highlight = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return new Set(agents.filter(a =>
      `${a.nome||''} ${a.ruolo||''} ${a.codice||''}`.toLowerCase().includes(q)
    ).map(a=>a.id));
  }, [query, agents]);

  const chars = useMemo(() => {
    const out = [];
    agents.forEach(u => {
      const p = positions[u.id];
      if (!p) return;
      out.push({ u, foot: tileFoot(p[0], p[1]), depth: p[0]+p[1], col: charColors[u.id] || defColors(u.id) });
    });
    out.sort((a,b)=>a.depth-b.depth);
    return out;
  }, [agents, positions, charColors]);

  const nAttivi = agents.filter(a=>a.stato==='attivo').length;
  const pbBusy  = playbooks.filter(p=>p.trigger_attivo).length;
  const lastLaunch = feed[0];

  return (
    <div ref={rootRef} style={{ position:'fixed', inset:0, zIndex:500, background:'#0D0D1F',
      display:'flex', flexDirection:'column', overflow:'hidden' }}>

      {/* ══ TOP HUD ══ */}
      <div style={{ position:'absolute', top:0, left:0, right:0, zIndex:20,
        display:'flex', alignItems:'center', gap:10, padding:'10px 14px', flexWrap:'wrap',
        background:'linear-gradient(180deg, rgba(7,7,22,0.85) 0%, rgba(7,7,22,0) 100%)', pointerEvents:'none' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, pointerEvents:'auto' }}>
          <button onClick={()=>navigate('/eden-hq')} title="Torna a Eden HQ"
            style={{ display:'flex', alignItems:'center', gap:6, background:'rgba(7,7,26,0.78)',
              border:'1px solid rgba(255,255,255,0.12)', borderRadius:9, padding:'7px 12px',
              cursor:'pointer', color:'#fff', fontSize:12, fontWeight:600, backdropFilter:'blur(6px)' }}>
            <ArrowLeft size={14}/> Esci
          </button>
          <div style={{ fontSize:16, fontWeight:900, color:'#00D4FF', letterSpacing:'.04em', textShadow:'0 0 14px rgba(0,212,255,0.6)' }}>
            EDEN HQ <span style={{ fontSize:10, fontWeight:600, color:'rgba(255,255,255,0.4)', letterSpacing:'.1em' }}>OFFICE</span>
          </div>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', pointerEvents:'auto' }}>
          <Chip label="Agenti" value={`${nAttivi}/${agents.length}`} color="#10B981"/>
          <Chip label="Playbook" value={playbooks.length}/>
          <Chip label="In corso" value={pbBusy} color={pbBusy>0?'#F59E0B':'rgba(255,255,255,0.5)'}/>
        </div>

        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:8, pointerEvents:'auto' }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, background:'rgba(7,7,26,0.78)',
            border:'1px solid rgba(255,255,255,0.12)', borderRadius:9, padding:'6px 10px', backdropFilter:'blur(6px)' }}>
            <Search size={13} color="rgba(255,255,255,0.4)"/>
            <input value={query} onChange={e=>setQuery(e.target.value)} placeholder="Cerca agente…"
              style={{ background:'none', border:'none', outline:'none', color:'#fff', fontSize:12, width:120 }}/>
            {query && <button onClick={()=>setQuery('')} style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(255,255,255,0.4)', display:'flex', padding:0 }}><X size={12}/></button>}
          </div>
          <div style={{ fontSize:13, fontWeight:700, color:'rgba(255,255,255,0.75)', fontFamily:'monospace',
            background:'rgba(7,7,26,0.78)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:9, padding:'6px 11px', backdropFilter:'blur(6px)' }}>
            {clock.toLocaleTimeString('it-IT',{hour:'2-digit',minute:'2-digit'})}
          </div>
          <button onClick={()=>loadData(false)} title="Aggiorna"
            style={{ display:'flex', background:'rgba(7,7,26,0.78)', border:'1px solid rgba(255,255,255,0.12)',
              borderRadius:9, padding:8, cursor:'pointer', color:'rgba(255,255,255,0.7)', backdropFilter:'blur(6px)' }}>
            <RefreshCw size={14}/>
          </button>
          <button onClick={toggleFs} title={isFs?'Esci da schermo intero':'Schermo intero'}
            style={{ display:'flex', background:'rgba(7,7,26,0.78)', border:'1px solid rgba(255,255,255,0.12)',
              borderRadius:9, padding:8, cursor:'pointer', color:'rgba(255,255,255,0.7)', backdropFilter:'blur(6px)' }}>
            {isFs ? <Minimize2 size={14}/> : <Maximize2 size={14}/>}
          </button>
        </div>
      </div>

      {/* ══ ROOM ══ */}
      <div style={{ flex:1, minHeight:0 }}>
        {loading ? (
          <div style={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center',
            color:'rgba(255,255,255,0.4)', fontSize:14 }}>Caricamento ufficio…</div>
        ) : (
          <RoomSVG
            chars={chars}
            layout={layout}
            busyMap={busyMap}
            highlight={highlight}
            onCharClick={ag=>{ setShowPb(false); setSelectedAg(ag); }}
            onWbClick={()=>{ setSelectedAg(null); setShowPb(true); }}
            pbCount={playbooks.length}
            pbBusy={pbBusy}
          />
        )}
      </div>

      {/* ══ BOTTOM HUD ══ */}
      <div style={{ position:'absolute', left:0, right:0, bottom:0, zIndex:20,
        display:'flex', alignItems:'flex-end', justifyContent:'space-between', gap:12,
        padding:'10px 14px', pointerEvents:'none',
        background:'linear-gradient(0deg, rgba(7,7,22,0.85) 0%, rgba(7,7,22,0) 100%)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap', pointerEvents:'auto' }}>
          {DEPTS.map(d=>(
            <span key={d.key} style={{ display:'flex', alignItems:'center', gap:5, fontSize:10.5, color:'rgba(255,255,255,0.55)' }}>
              <span style={{ width:8, height:8, borderRadius:2, background:d.color, display:'inline-block' }}/>
              {d.emoji} {d.label}
            </span>
          ))}
          <span style={{ fontSize:10, color:'rgba(255,255,255,0.3)' }}>· rotella = zoom · trascina = sposta · lavagna in alto a sinistra = playbook</span>
        </div>
        {lastLaunch && (
          <div style={{ pointerEvents:'auto', fontSize:11, color:'rgba(255,255,255,0.55)',
            background:'rgba(7,7,26,0.78)', border:'1px solid rgba(255,255,255,0.09)',
            borderRadius:9, padding:'6px 12px', backdropFilter:'blur(6px)', whiteSpace:'nowrap' }}>
            <span style={{ color:'#F59E0B' }}>⚡ ultimo lancio</span>{' '}
            {lastLaunch.utente_nome || lastLaunch.utente_email || 'sistema'}{' · '}
            {lastLaunch.creato_il ? new Date(lastLaunch.creato_il).toLocaleString('it-IT',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}) : ''}
          </div>
        )}
      </div>

      {/* ══ TOAST ══ */}
      {toast && (
        <div style={{ position:'absolute', bottom:54, left:'50%', transform:'translateX(-50%)', zIndex:50,
          background: toast.ok ? 'rgba(6,40,30,0.95)' : 'rgba(45,12,12,0.95)',
          border: `1px solid ${toast.ok ? 'rgba(16,185,129,0.5)' : 'rgba(220,38,38,0.5)'}`,
          color: toast.ok ? '#34D399' : '#F87171', fontSize:12.5, fontWeight:600,
          borderRadius:10, padding:'10px 18px', maxWidth:'min(560px, calc(100vw - 32px))',
          boxShadow:'0 8px 30px rgba(0,0,0,0.5)', backdropFilter:'blur(8px)' }}>
          {toast.ok ? '✓ ' : '✗ '}{toast.msg}
        </div>
      )}

      {/* ══ OVERLAYS ══ */}
      {showPb && (
        <PlaybookDrawer playbooks={playbooks} lanciando={lanciando} onLancia={lancia} onClose={()=>setShowPb(false)} isAdmin={isAdmin}/>
      )}
      {selectedAg && (
        <AgentPanel
          ag={agents.find(a=>a.id===selectedAg.id) || selectedAg}
          col={charColors[selectedAg.id] || defColors(selectedAg.id)}
          dept={layout.assign[selectedAg.id]}
          playbooks={playbooks}
          busyMap={busyMap}
          lanciando={lanciando}
          onLancia={lancia}
          onCustomize={()=>{ setCustUid(selectedAg.id); setSelectedAg(null); }}
          onClose={()=>setSelectedAg(null)}
          isAdmin={isAdmin}
        />
      )}
      {custUid && (
        <CharCustomizer
          uid={custUid}
          agName={agents.find(a=>a.id===custUid)?.nome||''}
          initial={charColors[custUid]||defColors(custUid)}
          onSave={(uid,cfg)=>setCharColors(prev=>({...prev,[uid]:cfg}))}
          onClose={()=>setCustUid(null)}
        />
      )}
    </div>
  );
}
